<template>
   <div>
      <navbar-component />
      <main class="bg-blueGray-100">
         <section class="relative overflow-hidden pt-16 text-white" :style="{ backgroundColor: '#111827' }">
            <div class="absolute inset-0 bg-center bg-cover" :style="{ backgroundImage: `url(${capabilitiesImage})`, opacity: 0.3 }"></div>
            <div class="absolute inset-0" :style="{ backgroundColor: 'rgba(17, 24, 39, 0.75)' }"></div>
            <div class="container relative mx-auto px-6 lg:px-8 py-24 md:py-28">
               <div class="max-w-4xl">
                  <h1 class="text-4xl md:text-5xl font-bold leading-tight mb-5">
                     WaveEdge powers acquisition, control, and connectivity at the edge.
                  </h1>
                  <p class="text-lg text-blueGray-200 leading-relaxed">
                     One industrial edge runtime powers our next-generation control platform with easier configuration, headless controllers, secure command structures, and broad connectivity for data acquisition and equipment control.
                  </p>
               </div>
            </div>
         </section>

         <section class="py-16 md:py-20 bg-blueGray-800">
            <div class="container mx-auto px-6 lg:px-8">
               <div class="grid lg:grid-cols-3 gap-6 lg:gap-8">
                  <div
                     v-for="positioning in positioningCards"
                     :key="positioning.title"
                     class="bg-blueGray-900 rounded-xl p-6 md:p-7 border border-blueGray-700 h-full"
                  >
                     <div class="w-12 h-12 rounded-full bg-blueGray-700 text-lightBlue-300 flex items-center justify-center mb-4">
                        <i :class="positioning.icon"></i>
                     </div>
                     <h3 class="text-xl font-bold text-white mb-2">{{ positioning.title }}</h3>
                     <p class="text-blueGray-200">{{ positioning.text }}</p>
                  </div>
               </div>
            </div>
         </section>

         <section id="capabilities" class="py-16 md:py-20 bg-blueGray-100">
            <div class="container mx-auto px-6 lg:px-8">
               <div class="text-center mb-12">
                  <h2 class="text-4xl font-bold text-blueGray-800 mb-3">Major capabilities</h2>
                  <p class="text-lg text-blueGray-600 max-w-3xl mx-auto">
                     Channel-based setup, broad industrial I/O, and plugin-driven machine behavior in one operational pattern.
                  </p>
               </div>
               <div class="grid lg:grid-cols-2 gap-6 lg:gap-8">
                  <div
                     v-for="feature in coreFeatures"
                     :key="feature.title"
                     :class="['rounded-xl p-5 md:p-6 border border-blueGray-700 shadow-md h-full bg-blueGray-800', feature.cardClass]"
                  >
                     <h3 class="text-xl font-bold text-white mb-2">{{ feature.title }}</h3>
                     <p class="text-blueGray-300 mb-3">{{ feature.description }}</p>
                     <ul class="space-y-2">
                        <li v-for="point in feature.points" :key="point" class="text-blueGray-200 text-sm">
                           <i class="fas fa-check-circle text-emerald-500 mr-2"></i>{{ point }}
                        </li>
                     </ul>
                  </div>
               </div>
            </div>
         </section>

         <section class="py-16 md:py-20 bg-white">
            <div class="container mx-auto px-6 lg:px-8">
               <div class="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
                  <div>
                     <h2 class="text-4xl font-bold text-blueGray-800 mb-4">Connectivity and field buses</h2>
                     <p class="text-lg text-blueGray-600 mb-6">
                        WaveEdge brings in and sends out data across plant and fleet links while handling reconnect-oriented field behavior.
                     </p>
                     <div class="grid sm:grid-cols-2 gap-4 lg:gap-5">
                        <div
                           v-for="item in connectivity"
                           :key="item.title"
                           class="bg-blueGray-50 rounded-lg p-4 md:p-5 border border-blueGray-100 h-full"
                        >
                           <p class="font-bold text-blueGray-800 mb-1">{{ item.title }}</p>
                           <p class="text-sm text-blueGray-600">{{ item.text }}</p>
                        </div>
                     </div>
                  </div>

                  <div class="bg-blueGray-900 rounded-2xl overflow-hidden text-white h-full flex flex-col shadow-lg">
                     <img :src="simulationImage" alt="Simulation Lab environment" class="w-full h-48 md:h-56 object-cover opacity-90" />
                     <div class="p-7 md:p-8 flex-grow">
                        <h3 class="text-2xl font-bold mb-4">Simulation and validation before shipping</h3>
                        <ul class="space-y-3 text-blueGray-100">
                           <li v-for="item in simulationBenefits" :key="item">
                              <i class="fas fa-angle-right mr-2 text-emerald-300"></i>{{ item }}
                           </li>
                        </ul>
                     </div>
                  </div>
               </div>
            </div>
         </section>

         <section class="py-16 md:py-20 bg-blueGray-100">
            <div class="container mx-auto px-6 lg:px-8">
               <div class="grid lg:grid-cols-2 gap-6 lg:gap-8 mb-12">
                  <div class="bg-white rounded-xl p-6 md:p-7 border border-blueGray-100 h-full">
                     <h2 class="text-3xl font-bold text-blueGray-800 mb-4">Layered security model</h2>
                     <p class="text-blueGray-600 mb-4">
                        Remote and sensitive behavior is controlled with license-validated command scope, authenticated sessions, and contextual formula-driven gating.
                     </p>
                     <ul class="space-y-2">
                        <li v-for="item in securityLayers" :key="item" class="text-blueGray-700">
                           <i class="fas fa-shield-alt text-blue-600 mr-2"></i>{{ item }}
                        </li>
                     </ul>
                  </div>
                  <div class="bg-white rounded-xl p-6 md:p-7 border border-blueGray-100 h-full">
                     <h2 class="text-3xl font-bold text-blueGray-800 mb-4">Outcomes for operations teams</h2>
                     <ul class="space-y-2">
                        <li v-for="item in outcomes" :key="item" class="text-blueGray-700">
                           <i class="fas fa-check text-emerald-500 mr-2"></i>{{ item }}
                        </li>
                     </ul>
                  </div>
               </div>
               
               <div class="bg-blueGray-800 rounded-2xl p-8 md:p-10 border border-blueGray-700 text-center max-w-4xl mx-auto shadow-lg">
                  <h3 class="text-2xl md:text-3xl font-bold text-white mb-4">WaveEdge powers pumpLink7</h3>
                  <p class="text-blueGray-300 text-lg mb-6">
                     See how our industrial runtime comes to life in a ruggedized, deployment-ready hardware platform.
                  </p>
                  <router-link to="/pumplink7" class="inline-block bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200">
                     Explore pumpLink7 <i class="fas fa-arrow-right ml-2"></i>
                  </router-link>
               </div>
            </div>
         </section>

      </main>
      <footer-component />
   </div>
</template>

<script>
import NavbarComponent from "@/components/Navbars/IndexNavbar.vue";
import FooterComponent from "@/components/Footers/Footer.vue";
import { setPageSeo } from "@/utils/seo";
import capabilitiesImage from "@/assets/img/waveedge-capabilities-grid.png";
import simulationImage from "@/assets/img/waveedge-simulation-lab.png";

export default {
   name: "WaveEdge",
   components: {
      NavbarComponent,
      FooterComponent,
   },
   data() {
      return {
         capabilitiesImage,
         simulationImage,
         // Positioning cards anchor the top narrative.
         positioningCards: [
            {
               title: "Industrial runtime",
               text: "Linux based controls on Opto22 hardware for field I/O, edge logic, logging, alarms, and cloud/SCADA integration.",
               icon: "fas fa-cogs",
            },
            {
               title: "Channel-based setup",
               text: "Structured channel and system configuration scales from one machine to fleet deployment patterns.",
               icon: "fas fa-project-diagram",
            },
            {
               title: "Plugin Design",
               text: "Product-specific machine behavior ships as plugins instead of runtime forks, keeping base platform features while easily delivering unique custom logic.",
               icon: "fas fa-puzzle-piece",
            },
         ],
         // Core feature sections for product messaging.
         coreFeatures: [
            {
               title: "Data and channels",
               description: "Declarative channel setup with formulas, scaling, data types, alarms, and totalizers.",
               cardClass: "border-l-4 border-l-cyan-500",
               points: [
                  "Manual, formula, trigger, total, and simulation sources",
                  "Floats, ints, strings, enums, bit unpacking, and arrays",
                  "Alarm limits with deadband and delay",
               ],
            },
            {
               title: "Control and automation",
               description: "Built-in control functions run in the same cycle as channels and plugin logic.",
               cardClass: "border-l-4 border-l-emerald-500",
               points: [
                  "PID with bumpless transfer, limits, and optional feedforward",
                  "WebHMIs can be hardware based or headless for remote operation.",
                  "Plugin based physical simulation of equipment for testing and training",
               ],
            },
            {
               title: "Logging and operations",
               description: "Operational continuity features for long-running field systems.",
               cardClass: "border-l-4 border-l-amber-500",
               points: [
                  "CSV batching, split-by-size, retention, and startup recovery",
                  "Rotating app logs with configurable levels and debug buffers",
                  "Config reload with compatible state preservation",
               ],
            },
            {
               title: "Cloud and SCADA readiness",
               description: "Edge-first behavior with reconnect and data continuity for unstable links.",
               cardClass: "border-l-4 border-l-indigo-500",
               points: [
                  "Multi-broker MQTT with per-broker channel sets and rates",
                  "Offline caching in memory and disk with reconnect flush",
                  "MQTT commands for status, config, restarts, and plugin actions",
               ],
            },
         ],
         // Connectivity blocks keep protocol messaging concise.
         connectivity: [
            {
               title: "MQTT",
               text: "Multi-broker publish, subscribe-as-input, SSL options, and LWT visibility.",
            },
            {
               title: "Modbus",
               text: "TCP and RTU support with threaded polling, read/write registers, coils, and endian-aware decoding.",
            },
            {
               title: "Serial in / serial out",
               text: "Native serial ingest and serial output for proprietary or standards-based device links.",
            },
            {
               title: "TCP streaming",
               text: "Delimited stream parsing and multiple TCP output connections in client or server mode.",
            },
            {
               title: "Opto 22 groov",
               text: "Multiple groov devices with resilience for drop-and-return behavior.",
            },
            {
               title: "CAN bus",
               text: "Live I/O mapping, CANtrace recording, and CANDATA playback for bench and regression testing.",
            },
         ],
         // Simulation bullets highlight pre-ship validation value.
         simulationBenefits: [
            "Equipment-level simulation plugins model realistic equipment behavior, not static fake numbers.",
            "Alarm and interlock override paths support safe lab and factory acceptance testing.",
            "Scenario runs cover startup, trips, recovery, and remote/local handoff behavior.",
            "CANtrace plus CANDATA replay makes defects reproducible for troubleshooting and regression.",
         ],
         // Security bullets reflect layered governance claims.
         securityLayers: [
            "License validation acts as a trust anchor for command capability.",
            "Login/logout sessions protect write and administrative command paths.",
            "Formula-driven predicates can gate plugin actions using live channel data and system parameters.",
            "Policy is encoded as configuration for product and customer variants without core forks.",
         ],
         // Outcome bullets focus on customer value.
         outcomes: [
            "Single edge app for acquisition, control, alarms, and outputs.",
            "Cloud- and SCADA-ready telemetry with reconnect and cache behavior.",
            "Faster productization through channel setup plus plugin extension.",
            "Improved commissioning and support with remote visibility and controlled operations.",
            "Pre-ship confidence from simulation, replay, and repeatable scenario testing.",
         ],
      };
   },
   mounted() {
      // Keep browser title and meta aligned with the product page.
      setPageSeo({
         title: "WaveEdge | Industrial Edge Runtime for Data Acquisition & Control | AMI",
         description:
            "WaveEdge is an industrial edge runtime for data acquisition, pump and equipment control, alarms, and secure connectivity—powering AMI products like pumpLink7 and Datavan MPC.",
         keywords:
            "WaveEdge, industrial edge runtime, edge computing, data acquisition, industrial I/O, SCADA edge, pump control, AMI",
      });
   },
};
</script>
